/**
 * The voices offered in the tag generator.
 *
 * Curated deliberately rather than proxied from `GET /v1/voices`:
 *
 *  - Every id here was verified to return audio on a Free-tier key. ElevenLabs
 *    gates *Voice Library* voices behind a paid plan (402 `paid_plan_required`)
 *    while leaving premade voices open, and that boundary is invisible until a
 *    merchant clicks Generate. Shipping only known-good ids means the picker
 *    cannot offer something that fails.
 *  - Listing voices needs a `voices_read` scope. Hardcoding keeps the API key
 *    minimal — text-to-speech and nothing else.
 *  - A producer wants a handful of auditioned options, not several thousand
 *    community uploads.
 *
 * Re-verify with a real key before adding to this list; membership of the
 * premade set is ElevenLabs' call, not ours, and it has moved before (Rachel
 * was premade and is now library-gated).
 */
/**
 * Speed bounds enforced by ElevenLabs; outside this it returns 400. Verified
 * against the live API, not taken from the docs.
 */
export const MIN_SPEED = 0.7;
export const MAX_SPEED = 1.2;

export interface VoiceTagVoice {
  id: string;
  name: string;
  gender: 'male' | 'female';
  /** One word a producer can choose on. Never "deep" — that word is taken
   *  by the render-time tag character, and they are not the same thing. */
  tone: string;
}

export const VOICE_TAG_VOICES: VoiceTagVoice[] = [
  { id: 'pNInz6obpgDQGcFmaJgB', name: 'Adam', gender: 'male', tone: 'Booming' },
  { id: 'ErXwobaYiN019PkySvjV', name: 'Antoni', gender: 'male', tone: 'Warm' },
  { id: 'JBFqnCBsd6RMkjVDRZzb', name: 'George', gender: 'male', tone: 'Rich' },
  { id: 'VR6AewLTigWG4xSOukaG', name: 'Arnold', gender: 'male', tone: 'Bold' },
  { id: 'EXAVITQu4vr4xnSDxMaL', name: 'Sarah', gender: 'female', tone: 'Bright' },
  { id: 'Xb7hH8MSUJpSbSDYk0k2', name: 'Alice', gender: 'female', tone: 'Clear' },
  { id: 'XrExE9yKIg1WjnnlVkGX', name: 'Matilda', gender: 'female', tone: 'Soft' },
  { id: 'pFZP5JQG7iQjIQuC4Bku', name: 'Lily', gender: 'female', tone: 'Youthful' },
  { id: 'cgSgspJ2msm6clMCkdW9', name: 'Jessica', gender: 'female', tone: 'Punchy' },
  { id: 'FGY2WhTYpPnrIDTdsKH5', name: 'Laura', gender: 'female', tone: 'Calm' },
];

export const isOfferedVoice = (voiceId: string): boolean =>
  VOICE_TAG_VOICES.some((voice) => voice.id === voiceId);
